// src/pages/admin/LecteurFormationDemo.jsx
// Composant de prévisualisation pour admin/client – utilise les endpoints ADMIN
import React, { useState, useEffect } from 'react';
import { APP_CONFIG } from '../../../config/appConfig';
import { BookOpen, Loader2, AlertCircle, Play, CheckCircle, Layers } from 'lucide-react';
import Selector from '../../../components/communs/Selector';

export default function LecteurFormationDemo({
    formationId,
    token,
    onClose,
    userRole = 'admin'
}) {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [showSelector, setShowSelector] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentContent, setCurrentContent] = useState(null);
    const [videoToken, setVideoToken] = useState(null);
    const [pdfToken, setPdfToken] = useState(null);

    // Récupération des modules via l'API admin
    useEffect(() => {
        const fetchModules = async () => {
            if (!formationId || !token) return;
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/admin/formations/${formationId}/modules`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        }
                    }
                );
                if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
                const data = await response.json();
                let modulesList = Array.isArray(data) ? data : (data.content || data.data || []);
                setModules(modulesList);
            } catch (err) {
                console.error('[LecteurFormationDemo] Erreur:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchModules();
    }, [formationId, token]);

    const getModuleType = (mod) => {
        if (mod?.typeModule) return mod.typeModule;
        if (mod?.hasPdf) return 'PDF';
        if (mod?.hasVideo) return 'VIDEO';
        if (mod?.hasJsonContent) return mod.typeModule === 'JSON_RICH' ? 'JSON_RICH' : 'JSON';
        if (mod?.hasEvaluation || mod?.evaluationId) return 'EVALUATION';
        if (mod?.pdfStreamUrl) return 'PDF';
        if (mod?.videoUrl) return 'VIDEO';
        if (mod?.jsonContent) return mod.typeModule === 'JSON_RICH' ? 'JSON_RICH' : 'JSON';
        return 'UNKNOWN';
    };

    // ---- Génération de token via endpoints ADMIN ----
    const generateVideoToken = async (moduleId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/video/token`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setVideoToken(data.token);
                return data.token;
            } else {
                console.error('Erreur génération token vidéo:', response.status);
                return null;
            }
        } catch (err) {
            console.error('[LecteurFormationDemo] Erreur génération token vidéo:', err);
            return null;
        }
    };

    const generatePdfToken = async (moduleId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/pdf/token`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setPdfToken(data.token);
                return data.token;
            } else {
                console.error('Erreur génération token PDF:', response.status);
                return null;
            }
        } catch (err) {
            console.error('[LecteurFormationDemo] Erreur génération token PDF:', err);
            return null;
        }
    };

    // ---- Récupération du contenu des modules (JSON, évaluations) ----
    const getModuleContenu = async (mod) => {
        if (!mod) return null;
        const type = getModuleType(mod);

        // Pour PDF/VIDEO, on ne récupère pas ici (géré dans openModule)
        if (type === 'PDF' || type === 'VIDEO') {
            return null;
        }

        if (type === 'JSON' || type === 'JSON_RICH' || type === 'EVALUATION') {
            try {
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${mod.id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        }
                    }
                );
                if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
                const detail = await response.json();
                if (type === 'EVALUATION') {
                    if (detail.evaluationId) {
                        const evalResponse = await fetch(
                            `${APP_CONFIG.BASE_URL}/api/v1/admin/evaluations/${detail.evaluationId}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Accept': 'application/json'
                                }
                            }
                        );
                        if (evalResponse.ok) {
                            const evalData = await evalResponse.json();
                            return [evalData];
                        }
                    }
                    return null;
                }
                return detail.jsonContent || detail.content || null;
            } catch (err) {
                console.error('Erreur chargement détail module:', err);
                return null;
            }
        }
        return null;
    };

    // ---- Ouverture d’un module ----
    const openModule = async (module, index) => {
        setCurrentIndex(index);
        const type = getModuleType(module);
        let contenu = null;
        let moduleToOpen = { ...module };

        if (type === 'VIDEO') {
            const videoAuthToken = await generateVideoToken(module.id);
            if (!videoAuthToken) {
                alert("⚠️ Impossible de générer un token pour cette vidéo.");
                return;
            }
            const videoStreamUrl =
                `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${module.id}/video/stream?token=${videoAuthToken}`;
            moduleToOpen.videoStreamUrl = videoStreamUrl;
            moduleToOpen.videoUrl = videoStreamUrl;
            setVideoToken(videoAuthToken);
            contenu = videoStreamUrl;

        } else if (type === 'PDF') {
            const pdfAuthToken = await generatePdfToken(module.id);
            if (pdfAuthToken) {
                const pdfStreamUrl =
                    `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${module.id}/pdf/stream?token=${pdfAuthToken}`;
                moduleToOpen.pdfStreamUrl = pdfStreamUrl;
                moduleToOpen.pdfUrl = pdfStreamUrl;
                setPdfToken(pdfAuthToken);
                contenu = pdfStreamUrl;
            } else {
                // Fallback : on utilise l'URL existante (si l’admin est authentifié, on peut stream sans token)
                contenu = module.pdfStreamUrl || module.pdfUrl || null;
                if (contenu) {
                    // Si le backend ne supporte pas le stream sans token, on génère un token même en fallback
                    // Mais ici on préfère laisser le Selector gérer
                }
            }

        } else {
            // JSON, JSON_RICH, EVALUATION
            contenu = await getModuleContenu(module);
            if (contenu === null) {
                alert("⚠️ Ce module n'a pas de contenu disponible.");
                return;
            }
            if (type === 'JSON' || type === 'JSON_RICH') {
                moduleToOpen.jsonContent = typeof contenu === 'object' ? contenu : null;
            } else if (type === 'EVALUATION') {
                moduleToOpen.evaluations = Array.isArray(contenu) ? contenu : undefined;
                moduleToOpen.content = Array.isArray(contenu) ? contenu : undefined;
            }
        }

        moduleToOpen.typeModule = type;
        setCurrentContent(contenu);
        setSelectedModule(moduleToOpen);
        setShowSelector(true);
    };

    // ---- Rendu ----
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-md px-4">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-sm text-gray-600">{error}</p>
                    <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Fermer</button>
                </div>
            </div>
        );
    }

    if (modules.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <BookOpen className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Aucun module dans cette formation</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 p-4 bg-white rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-gray-800">Prévisualisation de la formation</h2>
            <p className="text-sm text-gray-500">Mode démo – aucune progression n'est enregistrée</p>

            <div className="space-y-2">
                {modules.map((module, index) => {
                    const type = getModuleType(module);
                    return (
                        <div key={module.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-xs font-bold text-gray-700">
                                    {index + 1}
                                </span>
                                <span className="font-semibold text-gray-800 truncate">{module.titre}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                                    {type}
                                </span>
                            </div>
                            <button
                                onClick={() => openModule(module, index)}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                            >
                                <Play className="w-4 h-4 inline mr-1" /> Ouvrir
                            </button>
                        </div>
                    );
                })}
            </div>

            {selectedModule && (
                <Selector
                    module={selectedModule}
                    contenu={currentContent}
                    formationTitre="Prévisualisation"
                    moduleOrdre={currentIndex + 1}
                    totalModules={modules.length}
                    isOpen={showSelector}
                    onClose={() => setShowSelector(false)}
                    onModuleChange={async (direction) => {
                        const newIdx = currentIndex + direction;
                        if (newIdx >= 0 && newIdx < modules.length) {
                            await openModule(modules[newIdx], newIdx);
                        } else {
                            setShowSelector(false);
                        }
                    }}
                    onComplete={() => {}}
                    mode="demo"
                    userRole={userRole}
                    token={token}
                    pdfToken={pdfToken}
                    videoToken={videoToken}
                />
            )}
        </div>
    );
}